import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { useState, useEffect } from 'react';
import { db } from '../firebase';
interface UseFirebaseCartsResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

interface UseFirebaseAllCartsResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
}
/**
 * Custom hook to fetch a single cart by ID in real-time from Firebase Firestore
 * @param cartId - The cart ID to fetch
 * @returns Object containing data, loading state, and error
 */
export const useFirebaseCartById = (
  cartId: string
): UseFirebaseCartsResult<any> => {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!cartId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Reference to specific cart document
    const cartDocRef = doc(db, 'Carts', cartId);

    // Set up real-time listener for single document
    const unsubscribe = onSnapshot(
      cartDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setData({
            id: docSnap.id,
            ...docSnap.data()
          });
          setLoading(false);
          setError(null);
        } else {
          setData(null);
          setLoading(false);
          setError(new Error('Cart not found'));
        }
      },
      (err) => {
        console.error('❌ Error fetching cart by ID:', err);
        console.error('  Error code:', err.code);
        console.error('  Error message:', err.message);
        setError(err as Error);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      unsubscribe();
    };
  }, [cartId]);
  return { data, loading, error };
};

/**
 * Custom hook to fetch all carts for a specific restaurant in real-time from Firebase Firestore
 * @param restaurantId - The restaurant ID to filter carts by
 * @returns Object containing data (array of carts), loading state, and error
 */
export const useFirebaseAllCarts = (
  restaurantId: string
): UseFirebaseAllCartsResult<any> => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      setData([]);
      return;
    }

    setLoading(true);

    // Query carts from the Carts collection where restaurantIds array contains the restaurantId
    const q = query(
      collection(db, 'Carts'),
      where('restaurantIds', 'array-contains', restaurantId)
    );

    // Set up real-time listener for the collection
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const carts: any[] = [];
        querySnapshot.forEach((doc) => {
          carts.push({
            id: doc.id,
            ...doc.data()
          });
        });
        setData(carts);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('❌ Error fetching all carts:', err);
        console.error('  Error code:', err.code);
        console.error('  Error message:', err.message);
        setError(err as Error);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      unsubscribe();
    };
  }, [restaurantId]);

  return { data, loading, error };
};
